<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from './taskHelpers.js';
import { useTasksStore } from './useTasksStore.js';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'selectProject']);

const { t, locale } = useI18n();
const tasksStore = useTasksStore();
const { state } = tasksStore;
const query = ref('');

const projects = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase();
  return state.projects.filter(project => {
    const matchesContact = props.contactId
      ? String(project.contactId) === String(props.contactId)
      : true;
    const matchesSearch =
      !normalizedQuery || project.title.toLowerCase().includes(normalizedQuery);
    return matchesContact && matchesSearch;
  });
});

const selectProject = project => {
  emit('update:modelValue', project.id);
  emit('selectProject', project);
};
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col gap-3">
    <Input
      v-model="query"
      type="search"
      size="sm"
      :placeholder="t('TASKS.FORM.SEARCH_PROJECT')"
      :custom-input-class="['ltr:!pl-8 rtl:!pr-8']"
    >
      <template #prefix>
        <Icon
          icon="i-lucide-search"
          class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
        />
      </template>
    </Input>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <template v-if="projects.length">
        <button
          v-for="project in projects"
          :key="project.id"
          type="button"
          class="flex w-full items-center gap-3 border-b border-n-weak px-1 py-3 text-left last:border-b-0 hover:bg-n-alpha-1"
          :class="{ 'bg-n-brand/5': String(modelValue) === String(project.id) }"
          @click="selectProject(project)"
        >
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <span class="i-lucide-shopping-bag size-4 text-n-slate-11" />
          </span>
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ project.title }}
            </span>
            <span class="truncate text-xs text-n-slate-11">
              {{
                project.budget
                  ? formatCurrency(project.budget, project.currency, locale)
                  : t('TASKS.FORM.PROJECT_PRODUCTS', {
                      count: project.productsCount,
                    })
              }}
            </span>
          </span>
          <span
            v-if="String(modelValue) === String(project.id)"
            class="i-lucide-check size-4 shrink-0 text-n-brand"
          />
        </button>
      </template>

      <p v-else class="px-3 py-8 text-center text-sm text-n-slate-11">
        {{ t('TASKS.FORM.NO_PROJECTS') }}
      </p>
    </div>
  </div>
</template>
